import { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Box, Button, Card, Col, Grid, Heading, Spinner, Stack, Text, TextLink } from '@ui'
import { Link, useParams } from 'react-router-dom'
import { getJSON, patchJSON } from '../api.js'
import { APP_NAME } from '../brand.js'
import ProfileChart from '../components/ProfileChart.jsx'
import { PhotoGrid, PhotoViewer } from '../components/Photos.jsx'
import PhotoUpload from '../components/PhotoUpload.jsx'
import { GpxDownload } from '../components/GpxExport.jsx'
import ShareControl from '../components/ShareControl.jsx'
import { StarToggle } from '../components/Star.jsx'
import Splits from '../components/Splits.jsx'
import { ExcludeControl, NotesEditor } from '../components/WalkEdits.jsx'
import WalkMap from '../components/WalkMap.jsx'
import WalkWeather from '../components/WalkWeather.jsx'
import {
  COMPARISON_SOURCES, formatDate, formatDistance, formatDuration, formatNumber, formatPace, formatStride, formatTime,
  sourceLabel,
} from '../format.js'
import { placePhotos } from '../photoPins.js'
import { useUnit } from '../units.jsx'

export function Stat({ label, value, note }) {
  return (
    <Col xs={6} sm={4} lg={2}>
      <Box muted text="small">{label}</Box>
      <Box text="lg" weight="semibold">{value}</Box>
      {note && <Box muted text="small">{note}</Box>}
    </Col>
  )
}

// The recording's own climb, plus the barometer's when another phone's barometer drew the line
// (or only the barometer's, when the recording has none, as typed-in or some imported ones).
export function Climb({ walk, elevation }) {
  const own = walk.elevation_gain_meters
  const baro = elevation?.source === 'barometer' && !elevation.own ? `${formatNumber(elevation.gain_m)} m` : null
  if (own == null && baro) return <Stat label="Climb" value={baro} note={elevation.device ? `${elevation.device} barometer` : 'barometer'} />
  return <Stat label="Climb" value={own == null ? '–' : `${formatNumber(own)} m`} note={baro && `barometer ${baro}`} />
}

// "1 stop at 10:43", or "3 stops · longest 4 min at 10:43" (no time on a details-hidden link).
function stopsNote(stops, minutes) {
  const at = stops.longest_from_ms ? ` at ${formatTime(stops.longest_from_ms)}` : ''
  return stops.count === 1 ? `1 stop${at}` : `${stops.count} stops · longest ${minutes(stops.longest_ms)}${at}`
}

// Brisk minutes, cadence, stride and stops (2026-11-05). Brisk share, cadence and stride come from
// the step readings the chart uses (the track's `steps`: this recording's, or the same phone's
// Waystamp recording of the walk), so they're there only with readings; stops come from the route
// and the tile shows only when there was one (2 min or more within 25 m).
export function StepStats({ walk, track, unit }) {
  const steps = track?.steps
  const stops = track?.stops
  const brisk = walk.brisk_minutes ?? steps?.brisk_minutes
  const minutes = (ms) => `${Math.max(1, Math.round(ms / 60000))} min`
  return (
    <>
      {brisk != null && (
        <Stat
          label="Brisk minutes" value={formatNumber(brisk)}
          note={steps?.scored_minutes ? `of ${steps.scored_minutes} min · ${Math.round((100 * brisk) / steps.scored_minutes)}%` : undefined}
        />
      )}
      {steps?.cadence > 0 && <Stat label="Cadence" value={`${Math.round(steps.cadence)} /min`} note="steps a minute" />}
      {steps?.steps > 0 && walk.distance_meters > 0 && (
        <Stat label="Stride" value={formatStride(walk.distance_meters / steps.steps, unit)} />
      )}
      {stops && (
        <Stat
          label="Stopped" value={minutes(stops.total_ms)}
          note={stopsNote(stops, minutes)}
        />
      )}
    </>
  )
}

// Where the elevation line came from (the track's `elevation`, from the server): the chart title
// and a sentence under the chart. A barometer is levelled with the day's sea-level pressure
// (cmd/server/elevation.go); without it the line is height above the start.
export function elevationText(e) {
  if (e?.source !== 'barometer') return { label: undefined, short: undefined, note: null }
  // A shared page with details hidden doesn't name the phone (device is empty).
  const phone = e.own
    ? (e.device ? `this phone's barometer (${e.device})` : "the recording phone's barometer")
    : `the barometer of ${e.device ? `the ${e.device}` : 'a phone'}, which recorded the same walk`
  const baro = e.device ? `${e.device} barometer` : 'barometer'
  if (e.level === 'relative') {
    return {
      label: `Height above start (m, ${baro})`,
      short: 'Height above start (m, barometer)',
      note: `Elevation from ${phone}, shown as height above the start until the day's sea-level pressure is available.`,
    }
  }
  const source = e.level === 'met_office' ? 'Met Office' : 'Open-Meteo'
  return {
    label: `Elevation (m, ${baro}, ${source} pressure)`,
    short: 'Elevation (m, barometer)',
    note: e.level === 'met_office'
      ? `Elevation from ${phone}, levelled with the sea-level pressure observed at the Met Office station nearest the walk.`
      : `Elevation from ${phone}, levelled with Open-Meteo's sea-level pressure for the walk's time and place.`,
  }
}

// The chart's heading, and a sentence under it on where the steps a minute came from (the track's
// `steps`, from the server: this recording's step readings, or another phone's of the same walk).
export function chartHeading(track) {
  return track?.steps ? 'Elevation, pace and steps' : 'Elevation and pace'
}

export function stepsText(s) {
  if (!s) return null
  const brisk = `${s.brisk_minutes} brisk ${s.brisk_minutes === 1 ? 'minute' : 'minutes'}`
  if (s.own) return `Steps a minute are over the trailing minute, from this phone's step counter; the shaded clock minutes are its ${brisk}.`
  const phone = s.device ? `the ${s.device}'s ${APP_NAME} recording` : `a ${APP_NAME} recording`
  return `Steps a minute are from ${phone} of the same walk (this recording has no step readings); the shaded clock minutes are ${brisk} over this recording's time.`
}

// "Waystamp · Recorded on Google Pixel 10a · API 37 · barometer · build 273": the app that made the
// recording, then the phone (as the app's walk page says it).
export function recordedOn(walk) {
  if (walk.source === 'mmw_import' || COMPARISON_SOURCES[walk.source]) {
    const app = walk.source === 'mmw_import' ? 'MapMyWalk' : COMPARISON_SOURCES[walk.source]
    // MMW's export doesn't say which phone recorded it; set by hand on a few walks.
    return walk.device_model ? `${app} · Recorded on ${walk.device_model}` : app
  }
  if (!walk.device_model) return `${APP_NAME} (device not recorded)`
  const parts = [APP_NAME, `Recorded on ${[walk.device_manufacturer, walk.device_model].filter(Boolean).join(' ')}`]
  if (walk.android_sdk_int) parts.push(`API ${walk.android_sdk_int}`)
  if (walk.has_barometer != null) parts.push(walk.has_barometer ? 'barometer' : 'no barometer')
  if (walk.app_version_code) parts.push(`build ${walk.app_version_code}`)
  return parts.join(' · ')
}

// Under the map: how photo pins were placed, and how many photos aren't on it.
function PinNote({ placed }) {
  const byRoute = placed.pins.some((p) => p.from === 'route')
  const n = placed.outside
  if (!byRoute && n === 0) return null
  return (
    <Text as="p" muted text="small" mt={1} mb={0}>
      {byRoute && 'Photos without a location of their own are placed on the route at the time they were taken. '}
      {n > 0 && (n === 1
        ? "One photo isn't on the map: it was taken outside the walk's time."
        : `${n} photos aren't on the map: they were taken outside the walk's time.`)}
    </Text>
  )
}

export default function Walk() {
  const { id } = useParams()
  const [unit] = useUnit()
  const [data, setData] = useState(null)
  const [track, setTrack] = useState(null)
  const [error, setError] = useState(null)
  const [hover, setHover] = useState(null)
  const [photos, setPhotos] = useState([])
  const [photoIndex, setPhotoIndex] = useState(null)

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller
    setData(null)
    setTrack(null)
    setError(null)
    setHover(null)
    setPhotos([])
    setPhotoIndex(null)
    getJSON(`/api/walks/${id}`, { signal }).then(setData)
      .catch((err) => { if (err.name !== 'AbortError') setError(err.message) })
    getJSON(`/api/walks/${id}/track`, { signal }).then(setTrack)
      .catch((err) => { if (err.name !== 'AbortError') setError(err.message) })
    // Photos are extra: if they fail to list, the rest of the page still shows.
    getJSON(`/api/walks/${id}/photos`, { signal }).then(setPhotos).catch(() => {})
    return () => controller.abort()
  }, [id])
  const placed = useMemo(() => placePhotos(photos, track), [photos, track])
  const [starBusy, setStarBusy] = useState(false)
  const setStar = (starred) => {
    setStarBusy(true)
    patchJSON(`/api/walks/${id}`, { starred }).then(setData).catch(() => {}).finally(() => setStarBusy(false))
  }

  if (error) return <Alert variant="danger">Couldn't load this walk: {error}</Alert>
  if (!data) return <Spinner />

  const { walk, group } = data
  const others = group.filter((g) => g.id !== walk.id)

  const reloadPhotos = () => getJSON(`/api/walks/${id}/photos`).then(setPhotos).catch(() => {})
  const videoCount = photos.filter((p) => p.media_type === 'video').length
  const gallery = (
    <>
      {photos.length > 0 && (
        <>
          <Heading level={2} size={5} mt={4}>
            {videoCount ? 'Photos and videos' : 'Photos'} <Text muted weight="normal">({photos.length})</Text>
          </Heading>
          <PhotoGrid photos={photos} onOpen={setPhotoIndex} />
          <PhotoViewer
            photos={photos} index={photoIndex} onIndex={setPhotoIndex} walkStart={walk.start_time}
            onUpdated={(changed, fields) => setPhotos(photos.map((p) => (p.id === changed ? { ...p, ...fields } : p)))}
            onDeleted={(gone) => {
              const rest = photos.filter((p) => p.id !== gone)
              setPhotos(rest)
              setPhotoIndex(rest.length ? Math.min(photoIndex, rest.length - 1) : null)
            }}
          />
        </>
      )}
      <PhotoUpload walkId={walk.id} onAdded={reloadPhotos} />
    </>
  )

  return (
    <>
      <Stack wrap gap={2} align="center" mb={2}>
        <TextLink to="/walks" me="auto">← All walks</TextLink>
        <Button as={Link} to={`/walks/${walk.previous_id}`} size="sm" variant="secondary" outline disabled={!walk.previous_id}>‹ Older</Button>
        <Button as={Link} to={`/walks/${walk.next_id}`} size="sm" variant="secondary" outline disabled={!walk.next_id}>Newer ›</Button>
      </Stack>

      <Heading size={3} mb={1}>
        {walk.name}
        <StarToggle large starred={data.starred} busy={starBusy} onChange={setStar} />
      </Heading>
      <Text as="p" muted mb={2}>
        {formatDate(walk.start_time)}, {formatTime(walk.start_time)}–{formatTime(walk.end_time)} · {recordedOn(walk)}
        {walk.started_at_departure && ' · started on setting off'}
        {walk.ended_at_arrival && ' · ended on getting back to the start'}
        {walk.phone_gps_from && new Date(walk.phone_gps_from) <= new Date(walk.end_time) && ` · phone GPS from ${formatTime(walk.phone_gps_from)}`}
      </Text>
      <Stack wrap gap={2} mb={3}>
        {walk.excluded_from_analysis && <Badge>Excluded{walk.exclusion_reason ? `: ${walk.exclusion_reason}` : ''}</Badge>}
        {walk.has_mock_location && <Badge>Test trace (mock location)</Badge>}
        {COMPARISON_SOURCES[walk.source] && (
          <Badge>Comparison recording: not counted in totals</Badge>
        )}
        {!walk.is_counted && !walk.excluded_from_analysis && !walk.has_mock_location && !COMPARISON_SOURCES[walk.source] && (
          <Badge>Duplicate recording: not counted in totals</Badge>
        )}
      </Stack>
      <NotesEditor key={`notes-${walk.id}`} walk={walk} onSaved={setData} />
      <ExcludeControl key={`exclude-${walk.id}`} walk={walk} onSaved={setData} />
      <ShareControl key={`share-${walk.id}`} walkId={walk.id} />
      {track?.raw_point_count > 0 && <GpxDownload walkId={walk.id} />}

      <Grid gap={3} mb={4}>
        <Stat label="Distance" value={formatDistance(walk.distance_meters, unit)} />
        <Stat label="Moving time" value={formatDuration(walk.moving_duration_ms)} />
        <Stat label="Pace" value={`${formatPace(walk.moving_duration_ms, walk.distance_meters, unit)} /${unit}`} />
        <Climb walk={walk} elevation={track?.elevation} />
        <Stat label="Steps" value={formatNumber(walk.steps)} />
        <StepStats walk={walk} track={track} unit={unit} />
        {walk.calories_kcal != null && <Stat label="Calories" value={formatNumber(walk.calories_kcal)} />}
      </Grid>

      {others.length > 0 && (
        <Card title="Also recorded as" mb={4}>
          <Box as="ul" mb={0}>
            {others.map((g) => (
              <li key={g.id}>
                <TextLink to={`/walks/${g.id}`}>{sourceLabel(g)}</TextLink>, {formatTime(g.start_time)}, {formatDistance(g.distance_meters, unit)}
                {g.is_counted && <Badge variant="success" ms={2} weight="normal">counted in totals</Badge>}
                {COMPARISON_SOURCES[g.source] && <Badge ms={2} weight="normal">comparison</Badge>}
              </li>
            ))}
          </Box>
        </Card>
      )}

      {!track ? (
        <Spinner size="sm" label="Loading route…" />
      ) : track.raw_point_count === 0 ? (
        <>
          <Text as="p" muted>
            No GPS route: this walk was typed in by hand
            {walk.source === 'mmw_import' ? ' in MapMyWalk (for example after the app crashed)' : ''}, so there's only its distance and time.
          </Text>
          {gallery}
        </>
      ) : (
        <>
          <WalkMap points={track.points} hover={hover} pins={placed.pins} onOpenPhoto={setPhotoIndex} walkStart={walk.start_time} />
          <PinNote placed={placed} />
          {gallery}
          <Heading level={2} size={5} mt={4}>{chartHeading(track)}</Heading>
          <ProfileChart
            points={track.points} unit={unit} hover={hover} onHover={setHover}
            photos={placed.pins.filter((p) => p.d != null)} onOpenPhoto={setPhotoIndex} walkStart={walk.start_time}
            startMillis={track.start_ms}
            elevationLabel={elevationText(track.elevation).label}
            elevationLabelShort={elevationText(track.elevation).short}
          />
          <Heading level={2} size={5} mt={4}>Splits</Heading>
          <Splits points={track.points} unit={unit} briskAt={track.steps?.brisk_at} />
          <Text as="p" muted text="small">
            {track.points.length.toLocaleString('en-GB')} of {track.raw_point_count.toLocaleString('en-GB')} GPS points shown
            (fixes worse than 30 m left out, the rest thinned for drawing).
            {elevationText(track.elevation).note && ` ${elevationText(track.elevation).note}`}
            {track.steps && ` ${stepsText(track.steps)}`}
            {Math.abs(track.distance_scale - 1) > 0.001 &&
              ` Distances along the route are scaled by ${track.distance_scale.toFixed(3)} to match MapMyWalk's own total, which runs slightly under its GPS points.`}
          </Text>
        </>
      )}
      <WalkWeather walkId={walk.id} unit={unit} />
    </>
  )
}
